import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  DialogContentText,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
} from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import TeacherFormModal from "../../../components/Modals/TeacherFormModal";
import {
  useTeacher,
  Teacher,
  TeacherFormData,
} from "../../../hooks/useTeacher";
import { useAuth } from "../../../contexts/AuthContext";
import { useSchoolIdForAdmin } from "../../../hooks/useSchoolIdForAdmin";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import EnhancedTable from "../../../components/common/EnhancedTable";
import CommonModal from "../../../components/common/CommonModal";
import ActionButtons from "../../../components/common/ActionButtons";
import { useError } from "../../../contexts/ErrorContext";
import { useSessionClass } from "../../../contexts/SessionClassContext";
import { useClasses } from "../../../hooks/useClasses";

const TeacherManagement: React.FC = () => {
  const { user, isSchoolAdmin } = useAuth();
  // Disable API call for school admin - they already have schoolId from user context
  const { data: schoolIdFromAdmin, isLoading: schoolIdLoading } = useSchoolIdForAdmin(
    isSchoolAdmin() ? undefined : user?.id
  );
  const { selectedClass, selectedSession } = useSessionClass();

  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showError, showSuccess } = useError();

  // Compute schoolId only when all data is ready
  const schoolId = useMemo(() => {
    return user?.schoolId || schoolIdFromAdmin || undefined;
  }, [user?.schoolId, schoolIdFromAdmin]);

  // Get sessionId and classId from selectedSession and selectedClass
  const sessionId = selectedSession?.id;
  const classId = selectedClass?.id;
  const isAllClassForApi =
    selectedClass?.section === "__GRADE_ALL__" ||
    selectedClass?.name === "ALL" ||
    String(selectedClass?.name || "").includes("-ALL");

  const effectiveClassId =
    !isAllClassForApi && typeof classId === "number" && classId > 0
      ? classId
      : undefined;

  // Fetch all classes for the school
  const { classesQuery } = useClasses(schoolId);
  const allClasses = classesQuery.data || [];

  const {
    teachersQuery,
    useStaffByStaffAndClass,
    useStaffClassMapping,
    createTeacherMutation,
    updateTeacherMutation,
    deleteTeacherMutation,
    deactivateTeacherMutation,
  } = useTeacher(schoolId, false);

  // Call the new API to get teachers by session and class
  const staffByStaffAndClassQuery = useStaffByStaffAndClass(sessionId, effectiveClassId);

  // Call the API to get staff-class mappings by sessionId
  const staffClassMappingQuery = useStaffClassMapping(sessionId);

  // Call the API when teacher section is accessed (when sessionId and classId are available)
  useEffect(() => {
    if (sessionId && effectiveClassId && sessionId > 0 && effectiveClassId > 0) {
      // Force refetch the API when teacher section is accessed
      queryClient.refetchQueries({
        queryKey: ["staff", "staffAndClass", sessionId, effectiveClassId]
      });
    }
  }, [sessionId, effectiveClassId, queryClient]);

  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentTeacher, setCurrentTeacher] = useState<
    (TeacherFormData & { id?: number }) | undefined
  >(undefined);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [teacherToDelete, setTeacherToDelete] = useState<number | null>(null);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [teacherToDeactivate, setTeacherToDeactivate] = useState<{ id: number; enabled: boolean } | null>(null);
  const [showAllTeachers, setShowAllTeachers] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    setShowAllTeachers(false);
  }, [selectedClass?.id, selectedClass?.section, selectedSession?.id]);

  const allSchoolTeachers = useMemo(() => {
    if (!teachersQuery.data) return [];
    return teachersQuery.data.filter((teacher) => teacher.status === true);
  }, [teachersQuery.data]);

  const handleToggleViewAllTeachers = () => {
    const next = !showAllTeachers;
    setShowAllTeachers(next);
    if (next && schoolId) {
      queryClient.refetchQueries({ queryKey: ["teachers", schoolId] });
    }
  };

  const columns = useMemo(
    () => [
      { id: "firstName", label: t("first_name"), minWidth: 130 },
      { id: "lastName", label: t("last_name"), minWidth: 130 },
      { id: "employeeId", label: t("employee_id"), minWidth: 100 },
      { id: "email", label: t("email"), minWidth: 170 },
      { id: "department", label: t("subject"), minWidth: 130 },
    ],
    [t]
  );

  // Get teachers directly from the new API response
  const schoolTeachers = useMemo(() => {
    if (!staffByStaffAndClassQuery.data) {
      return [];
    }

    // Filter out inactive/deleted teachers
    return staffByStaffAndClassQuery.data.filter((teacher) => teacher.status === true);
  }, [staffByStaffAndClassQuery.data]);

  const hasClassSelected =
    !!selectedClass?.name && !!selectedClass?.section;

  const tableTeachers = showAllTeachers ? allSchoolTeachers : schoolTeachers;
  const isTableLoading = showAllTeachers
    ? teachersQuery.isLoading
    : !!(sessionId && effectiveClassId && staffByStaffAndClassQuery.isLoading);
  const tableError = showAllTeachers
    ? teachersQuery.error
    : staffByStaffAndClassQuery.error;

  const handleOpenAddModal = () => {
    setIsEdit(false);
    setCurrentTeacher(undefined);
    setModalOpen(true);
  };

  const handleOpenEditModal = (teacher: Teacher) => {
    // Extract distinct classes for this teacher from staff-class mapping
    const teacherClasses: string[] = [];

    if (staffClassMappingQuery.data && teacher.id) {
      // Filter mappings for this specific teacher
      const teacherMappings = staffClassMappingQuery.data.filter(
        (mapping) => mapping.staffId === teacher.id || mapping.teacherId === teacher.id
      );

      // Extract distinct class names using a Set to avoid duplicates
      const classNamesSet = new Set<string>();

      teacherMappings.forEach((mapping) => {
        // First, try to get class info from the mapping directly
        let className = mapping.className || mapping.class?.name || mapping.name;
        let section = mapping.section || mapping.class?.section;

        // If not found in mapping, look up by classId in allClasses
        if (!className && mapping.classId && allClasses.length > 0) {
          const classInfo = allClasses.find(c => c.id === mapping.classId);
          if (classInfo) {
            className = classInfo.name;
            section = classInfo.section;
          }
        }

        if (className) {
          // Format: "className section" or just "className" if no section
          const displayName = section
            ? `${className} ${section}`
            : className;
          classNamesSet.add(displayName);
        }
      });

      teacherClasses.push(...Array.from(classNamesSet));
    }

    setIsEdit(true);
    setCurrentTeacher({
      ...teacher,
      teacherOfClass: teacherClasses,
    });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  // Helper function to extract user-friendly error message
  const extractErrorMessage = (error: unknown): string => {
    const err = error as any;
    const backendMessage = err?.response?.data?.message || err?.message || "";

    // Check for duplicate email error
    if (backendMessage) {
      const messageLower = backendMessage.toLowerCase();

      // Check for duplicate key constraint errors related to email
      if (messageLower.includes("duplicate key value violates unique constraint")) {
        // Check if it's an email constraint
        if (messageLower.includes("uk_staff_email") ||
          messageLower.includes("email") &&
          (messageLower.includes("constraint") || messageLower.includes("unique"))) {
          return t("email_already_exists_for_teacher");
        }
      }

      // Check for other email-related duplicate errors
      if (messageLower.includes("email") &&
        (messageLower.includes("already") ||
          messageLower.includes("exist") ||
          messageLower.includes("duplicate") ||
          messageLower.includes("taken"))) {
        return t("email_already_exists_for_teacher");
      }
    }

    return backendMessage || "";
  };

  const handleSubmit = async (formData: TeacherFormData) => {
    if (isEdit && currentTeacher?.id) {
      // Remove password field for edit operations
      const { password, ...formDataWithoutPassword } = formData;
      updateTeacherMutation.mutate(
        { id: currentTeacher.id, data: formDataWithoutPassword },
        {
          onError: (error: unknown) => {
            const errorMessage = extractErrorMessage(error);
            showError(errorMessage || t("error_updating_teacher"));
          },
          onSuccess: () => {
            // Invalidate the staff by staff and class query to refresh the data
            if (sessionId && classId) {
              queryClient.invalidateQueries({
                queryKey: ["staff", "staffAndClass", sessionId, classId]
              });
            }
            // Invalidate staff class mapping query to refresh teacher's class assignments
            if (sessionId) {
              queryClient.invalidateQueries({
                queryKey: ["staffClassMapping", sessionId]
              });
            }
            showSuccess(t("teacher_updated_successfully"));
            handleCloseModal();
          },
        }
      );
    } else {
      createTeacherMutation.mutate(formData, {
        onError: (error: unknown) => {
          const errorMessage = extractErrorMessage(error);
          showError(errorMessage || t("error_creating_teacher"));
        },
        onSuccess: () => {
          // Invalidate the staff by staff and class query to refresh the data
          if (sessionId && classId) {
            queryClient.invalidateQueries({
              queryKey: ["staff", "staffAndClass", sessionId, classId]
            });
          }
          showSuccess(t("teacher_created_successfully"));
          handleCloseModal();
        },
      });
    }
  };

  const handleOpenDeleteDialog = (id: number) => {
    setTeacherToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setTeacherToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (teacherToDelete !== null) {
      deleteTeacherMutation.mutate(teacherToDelete, {
        onError: (error: unknown) => {
          const err = error as any;
          const backendMessage = err?.response?.data?.message;
          showError(backendMessage || t("error_deleting_teacher"));
        },
        onSuccess: () => {
          // Invalidate the staff by staff and class query to refresh the data
          if (sessionId && classId) {
            queryClient.invalidateQueries({
              queryKey: ["staff", "staffAndClass", sessionId, classId]
            });
          }
          showSuccess(t("teacher_deleted_successfully"));
          handleCloseDeleteDialog();
        },
      });
    }
  };

  const handleOpenDeactivateDialog = (id: number, enabled: boolean) => {
    setTeacherToDeactivate({ id, enabled });
    setDeactivateDialogOpen(true);
  };

  const handleCloseDeactivateDialog = () => {
    setDeactivateDialogOpen(false);
    setTeacherToDeactivate(null);
  };

  const handleConfirmDeactivate = () => {
    if (teacherToDeactivate !== null) {
      deactivateTeacherMutation.mutate(teacherToDeactivate.id, {
        onError: (error: unknown) => {
          const err = error as any;
          const backendMessage = err?.response?.data?.message || err?.message;
          showError(backendMessage || t("error_deactivating_teacher"));
        },
        onSuccess: (response: any) => {
          // Invalidate the staff by staff and class query to refresh the data
          if (sessionId && classId) {
            queryClient.invalidateQueries({
              queryKey: ["staff", "staffAndClass", sessionId, classId]
            });
          }
          // The API returns a string message directly
          const message = typeof response === 'string' ? response : (response?.message || response?.data || t("teacher_status_updated_successfully"));
          showSuccess(message);
          handleCloseDeactivateDialog();
        },
      });
    }
  };

  // Show loading state if data is not ready
  // Only check schoolIdLoading if user doesn't already have schoolId (for non-school-admin users)
  const shouldWaitForSchoolId = !user?.schoolId && schoolIdLoading;
  if (shouldWaitForSchoolId) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  // Show error if no school ID found (don't show loader)
  if (!schoolId) {
    return (
      <Paper sx={{ p: 2, maxWidth: 1500, mx: "auto", mt: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
          <Typography variant="body1" color="error">
            {t("no_school_found_for_admin")}
          </Typography>
        </Box>
      </Paper>
    );
  }

  // Show message if class is not selected (unless viewing all teachers)
  if (!hasClassSelected && !showAllTeachers) {
    return (
      <Paper sx={{ p: 1.5, maxWidth: 1500, mx: "auto", mt: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.1rem' }}>
            {t("teacher_management")}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {isSchoolAdmin() && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<VisibilityIcon />}
                onClick={handleToggleViewAllTeachers}
                sx={{
                  borderColor: "#3092e3",
                  color: "#3092e3",
                  "&:hover": {
                    borderColor: "#1976d2",
                    backgroundColor: "rgba(48, 146, 227, 0.04)",
                  },
                }}
              >
                {t("view_all_teachers") || "View All Teachers"}
              </Button>
            )}
            <Button
              variant="contained"
              size="small"
              sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
              onClick={handleOpenAddModal}
            >
              {t("add_teacher")}
            </Button>
          </Box>
        </Box>
        <Alert severity="info" sx={{ mb: 1 }}>
          {t("please_select_a_class_to_view_teachers")}
        </Alert>
      </Paper>
    );
  }

  if (isTableLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  const formattedData = tableTeachers.map((row: any) => ({
    ...row,
    department: row.department || "",
  }));

  const renderActions = (row: any) => {
    // Check if teacher is enabled/active - fetch enabled status from row
    const isEnabled = row.enabled === true;

    return (
      <>
        <Tooltip title={isEnabled ? t("deactivate") : t("enable")}>
          <IconButton
            size="small"
            onClick={() => handleOpenDeactivateDialog(row.id, row.enabled === true)}
            sx={{ color: isEnabled ? 'warning.main' : 'success.main' }}
          >
            <BlockIcon />
          </IconButton>
        </Tooltip>
        <ActionButtons
          onEdit={() => handleOpenEditModal(row)}
          onDelete={() => handleOpenDeleteDialog(row.id)}
          editTooltip={t("edit")}
          deleteTooltip={t("delete")}
        />
      </>
    );
  };

  return (
    <>
      <Paper sx={{ p: 1.5, maxWidth: 1500, mx: "auto", mt: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.25rem' }}>
            {t("teacher_management")}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            <TextField
              size="small"
              placeholder={t("search")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 180, '& .MuiInputBase-root': { height: 36 }}}
            />
            {isSchoolAdmin() && (
              <Button
                variant={showAllTeachers ? "contained" : "outlined"}
                size="small"
                startIcon={<VisibilityIcon />}
                onClick={handleToggleViewAllTeachers}
                sx={
                  showAllTeachers
                    ? { backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" }, height: 36 }
                    : {
                        borderColor: "#3092e3",
                        color: "#3092e3",
                        "&:hover": {
                          borderColor: "#1976d2",
                          backgroundColor: "rgba(48, 146, 227, 0.04)",
                        },
                        height: 36
                      }
                }
              >
                {showAllTeachers
                  ? t("view_by_class") || "View by Class"
                  : t("view_all_teachers") || "View All Teachers"}
              </Button>
            )}
            <Button
              variant="contained"
              size="small"
              sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" }, height: 36 }}
              onClick={handleOpenAddModal}
            >
              {t("add_teacher")}
            </Button>
          </Box>
        </Box>
        {showAllTeachers && (
          <Alert severity="info" sx={{ mb: 1 }}>
            {t("showing_all_teachers") || "Showing all teachers in the school"}
          </Alert>
        )}
        {tableError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {tableError?.message || "Error loading data"}
          </Alert>
        )}
        <EnhancedTable
          columns={columns}
          data={formattedData}
          searchFields={[
            "firstName",
            "lastName",
            "employeeId",
            "email",
            "department",
          ]}
          filterFields={[]}
          hideSearch
          externalSearchTerm={searchTerm}
          actions={renderActions}
          cardProps={{
            elevation: 0,
            sx: {
              border: 'none',
              boxShadow: 'none',
              backgroundColor: 'transparent',
                mb: 0,
                '& .MuiCardContent-root': {
                  p: 0,
                  '&:last-child': { pb: 0 }
                }
            }
          }}
        />
      </Paper>

      <TeacherFormModal
        open={modalOpen}
        handleClose={handleCloseModal}
        initialData={currentTeacher}
        onSubmit={handleSubmit}
        isEdit={isEdit}
        schoolId={schoolId ?? 0}
      />

      <CommonModal
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        title={t("confirm_delete")}
        maxWidth="xs"
        danger
        submitLabel={t("delete")}
        loading={deleteTeacherMutation.isLoading}
        onSubmit={handleConfirmDelete}
      >
        <DialogContentText>
          {t("are_you_sure_you_want_to_delete_this_teacher")}
        </DialogContentText>
      </CommonModal>

      <CommonModal
        open={deactivateDialogOpen}
        onClose={handleCloseDeactivateDialog}
        title={teacherToDeactivate?.enabled ? t("confirm_deactivate") : t("confirm_enable")}
        maxWidth="xs"
        customActions={
          <>
            <Button
              onClick={handleCloseDeactivateDialog}
              variant="outlined"
              sx={{ borderColor: "#3092e3", color: "#3092e3", "&:hover": { borderColor: "#1a6fb5", color: "#1a6fb5", backgroundColor: "rgba(48,146,227,0.08)" } }}
            >
              {t("cancel")}
            </Button>
            <Button
              onClick={handleConfirmDeactivate}
              variant="contained"
              color={teacherToDeactivate?.enabled ? "warning" : "success"}
              disabled={deactivateTeacherMutation.isLoading}
            >
              {deactivateTeacherMutation.isLoading ? (
                <><CircularProgress size={16} sx={{ mr: 1 }} />{t('updating') || 'Updating...'}</>
              ) : (
                teacherToDeactivate?.enabled ? t("disable") : t("enable")
              )}
            </Button>
          </>
        }
      >
        <DialogContentText>
          {teacherToDeactivate?.enabled
            ? t("are_you_sure_you_want_to_disable_teacher")
            : t("are_you_sure_you_want_to_enable_teacher")}
        </DialogContentText>
      </CommonModal>
    </>
  );
};

export default TeacherManagement;
